// assets/size-modal-factory.js
// Generic factory to create Silon size-picker modals for different products.
// The full original implementation that powered the pillow-case picker has
// been extracted and made configurable so we can re-use it for the upcoming
// pillow picker as well.  Nothing about the existing behaviour changes –
// the old picker merely calls this factory with its current configuration.
// 
// HOW TO USE
// ---------------------------------------------------------------------------
//   const modal = window.SilonSizeModalFactory({
//     overlayId: 'size-modal',           // DOM id of the overlay element
//     productSizes: {                    // mapping → see below for structure
//       colorKey?: {
//        sizeKey: { id: '123', surcharge: 0 }
//       },
//       sizeKey?: { id: '123', surcharge: 0 }
//     },
//     colorBased: true,                  // set to false when there are NO colours
//     defaultSize: 'comfortplus'         // which size is selected initially
//   });
//   modal.show(/* args – same signature as before */);
// ---------------------------------------------------------------------------
(function(global){
  if (global.SilonSizeModalFactory) return; // Guard: already initialised

  function factory(config){
    const {
      overlayId,
      productSizes,
      colorBased = true,
      defaultSize = 'comfortplus'
    } = config;

    // Cache DOM references (will be populated after DOMContentLoaded)
    let overlay, heroImg,
        priceEl, priceValueEl,
        comparisonPriceEl, comparisonPriceValueEl,
        sizeInputs, addBtn;

    // Runtime state – gets reset on every .show()
    let currentColor, currentQty, currentSurcharge,
        currentActualPrice, currentComparisonPrice,
        variantData = {}; // Map of variantId -> { inventory_quantity, inventory_policy, inventory_management, available }

    /* ----------------------------------------------------------- */
    /*  INIT                                                        */
    /* ----------------------------------------------------------- */
    document.addEventListener('DOMContentLoaded', () => {
      overlay    = document.getElementById(overlayId);
      heroImg    = overlay?.querySelector('.silon-modal__hero');
      priceEl    = overlay?.querySelector('.silon-modal__actual-price');
      priceValueEl = overlay?.querySelector('.actual-price-value');
      comparisonPriceEl = overlay?.querySelector('.silon-modal__comparison-price');
      comparisonPriceValueEl = overlay?.querySelector('.comparison-price-value');
      sizeInputs = overlay?.querySelectorAll('input[name="size"]') || [];
      addBtn     = overlay?.querySelector('.silon-btn');

      sizeInputs.forEach(input => {
        input.addEventListener('change', onSizeChange);
        // Prevent clicking unavailable cards
        const card = input.closest('.silon-card');
        if (card) {
          card.addEventListener('click', (e) => {
            if (card.classList.contains('silon-card--unavailable')) {
              e.preventDefault();
              e.stopPropagation();
            }
          });
        }
      });
      overlay?.addEventListener('click', onOverlayClick);
      addBtn?.addEventListener('click', onAddClick);

      preloadHero();
      
      // Load variant data from Liquid on DOM ready
      loadVariantData();
    });

    /* ----------------------------------------------------------- */
    /*  HELPERS                                                     */
    /* ----------------------------------------------------------- */
    function preloadHero(){
      if (!heroImg || heroImg.src || !heroImg.dataset.src) return;
      const img = new Image();
      img.onload = () => heroImg.src = heroImg.dataset.src;
      img.src = heroImg.dataset.src;
    }

    function onOverlayClick(e){
      if (e.target === overlay || e.target.classList.contains('silon-modal__close')){
        overlay.classList.remove('silon-overlay--visible');
        document.body.classList.remove('overflow-hidden');
      }
    }

    function setLoading(isLoading){
      if (!addBtn) return;
      if (isLoading){
        addBtn.disabled = true;
        addBtn.classList.add('silon-btn--loading');
        addBtn.innerHTML = `
          <svg class="silon-btn__spinner" width="16" height="16" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-dasharray="31.416" stroke-dashoffset="31.416">
              <animate attributeName="stroke-dasharray" dur="2s" values="0 31.416;15.708 15.708;0 31.416" repeatCount="indefinite"/>
              <animate attributeName="stroke-dashoffset" dur="2s" values="0;-15.708;-31.416" repeatCount="indefinite"/>
            </circle>
          </svg>
          <span>Wird hinzugefügt...</span>`;
      } else {
        addBtn.disabled = false;
        addBtn.classList.remove('silon-btn--loading');
        addBtn.innerHTML = 'In den Warenkorb';
      }
    }

    function getMapping(sizeKey){
      return colorBased ? productSizes[currentColor]?.[sizeKey] : productSizes[sizeKey];
    }

    function loadVariantData(){
      // Load variant data from Liquid-provided JSON (preferred method)
      const variantDataEl = document.getElementById('size-modal-variant-data');
      if (variantDataEl) {
        try {
          variantData = JSON.parse(variantDataEl.textContent);
          return;
        } catch (e) {
          console.warn('[SilonSizeModal] Failed to parse variant data from Liquid');
        }
      }
      
      // Fallback: Try to get from product JSON if available
      let productData = null;
      const productJsonEl = document.querySelector('[data-product-json]');
      if (productJsonEl) {
        try {
          productData = JSON.parse(productJsonEl.textContent);
        } catch (e) {
          console.warn('[SilonSizeModal] Failed to parse product JSON');
        }
      }
      
      if (productData && productData.variants) {
        variantData = {};
        productData.variants.forEach(variant => {
          variantData[String(variant.id)] = {
            inventory_quantity: variant.inventory_quantity || 0,
            inventory_policy: variant.inventory_policy || 'deny',
            inventory_management: variant.inventory_management,
            available: variant.available !== false
          };
        });
      }
    }

    function checkVariantAvailability(variantId, requestedQty){
      const variantIdStr = String(variantId);
      const data = variantData[variantIdStr];
      
      if (!data) {
        // If we don't have data, default to available
        return true;
      }
      
      // If "continue selling when out of stock" is enabled, always available
      if (data.inventory_policy === 'continue') {
        return true;
      }
      
      // If no inventory management, always available
      if (!data.inventory_management) {
        return true;
      }
      
      // For 'deny' policy, check if there's enough inventory for the requested quantity
      const inventoryQty = data.inventory_quantity != null ? Number(data.inventory_quantity) : 0;
      return inventoryQty >= requestedQty;
    }

    function getVariantAvailabilityMap(requestedQty){
      const variantIds = [];
      if (colorBased && currentColor && productSizes[currentColor]) {
        Object.values(productSizes[currentColor]).forEach(mapping => {
          if (mapping.id) variantIds.push(String(mapping.id));
        });
      } else if (!colorBased) {
        Object.values(productSizes).forEach(mapping => {
          if (mapping.id) variantIds.push(String(mapping.id));
        });
      }

      const availabilityMap = {};
      variantIds.forEach(id => {
        availabilityMap[id] = checkVariantAvailability(id, requestedQty);
      });

      return availabilityMap;
    }

    function markUnavailableVariants(availabilityMap){
      if (!sizeInputs || sizeInputs.length === 0) return;

      sizeInputs.forEach(input => {
        const mapping = getMapping(input.value);
        if (!mapping || !mapping.id) return;

        const card = input.closest('.silon-card');
        if (!card) return;

        const variantIdStr = String(mapping.id);
        const isAvailable = availabilityMap[variantIdStr] === true;
        
        if (!isAvailable) {
          card.classList.add('silon-card--unavailable');
          input.disabled = true;
          
          // Add "Ausverkauft" message if not already present
          const body = card.querySelector('.silon-card__body');
          if (body && !body.querySelector('.silon-card__sold-out')) {
            const soldOutEl = document.createElement('div');
            soldOutEl.className = 'silon-card__sold-out';
            soldOutEl.textContent = 'Ausverkauft';
            body.appendChild(soldOutEl);
          }
        } else {
          card.classList.remove('silon-card--unavailable');
          input.disabled = false;
          
          // Remove "Ausverkauft" message if present
          const soldOutEl = card.querySelector('.silon-card__sold-out');
          if (soldOutEl) soldOutEl.remove();
        }
      });
    }

    function selectFirstAvailableSize(availabilityMap){
      if (!sizeInputs || sizeInputs.length === 0) return;

      // Check if current default is available
      const currentDefault = overlay?.querySelector(`input[name="size"][value="${defaultSize}"]`);
      if (currentDefault) {
        const mapping = getMapping(defaultSize);
        if (mapping && mapping.id) {
          const variantIdStr = String(mapping.id);
          if (availabilityMap[variantIdStr] === true) {
            // Default is available, keep it selected
            currentDefault.checked = true;
            return;
          }
        }
      }

      // Default is unavailable, find first available
      for (const input of sizeInputs) {
        const mapping = getMapping(input.value);
        if (mapping && mapping.id) {
          const variantIdStr = String(mapping.id);
          if (availabilityMap[variantIdStr] === true) {
            input.checked = true;
            onSizeChange(); // Update price display
            return;
          }
        }
      }

      // If no variant is available, uncheck all
      sizeInputs.forEach(input => input.checked = false);
    }

    function onSizeChange(){
      const selected = Array.from(sizeInputs).find(i => i.checked);
      if (!selected) return;
      
      // Prevent selecting unavailable variants
      const mapping = getMapping(selected.value);
      if (!mapping){
        console.error('[SilonSizeModal] Unknown size', selected.value);
        selected.checked = false;
        return;
      }
      
      // Check if variant is available for current quantity
      const requestedQty = currentQty || 1;
      const isAvailable = checkVariantAvailability(mapping.id, requestedQty);
      
      if (!isAvailable) {
        selected.checked = false;
        // Try to select first available variant
        const availabilityMap = getVariantAvailabilityMap(requestedQty);
        selectFirstAvailableSize(availabilityMap);
        return;
      }
      
      currentSurcharge = mapping.surcharge || 0;
      updatePriceDisplay();
    }

    function updatePriceDisplay(){
      if (!priceValueEl) return;
      const base = currentActualPrice ?? 0;
      const totalActual = base + (currentQty * (currentSurcharge || 0));
      priceValueEl.textContent = totalActual;

      if (currentComparisonPrice){
        comparisonPriceEl?.classList.remove('price-hidden');
        comparisonPriceValueEl.textContent = currentComparisonPrice + (currentQty * (currentSurcharge || 0));
      } else {
        comparisonPriceEl?.classList.add('price-hidden');
      }
    }

    /* ----------------------------------------------------------- */
    /*  CART HANDLING (unchanged from original implementation)      */
    /* ----------------------------------------------------------- */
    async function addToCartAndRefresh(variantId, qty){
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', qty);
      const sectionsToRender = getSectionsToRender();
      formData.append('sections', sectionsToRender.map(s => s.section).join(','));
      formData.append('sections_url', window.location.pathname);

      const response = await fetch(`${routes.cart_add_url}`, {
        method: 'POST',
        headers: { 'X-Requested-With':'XMLHttpRequest', 'Accept':'application/javascript' },
        body: formData
      });
      const parsed = await response.json();
      if (parsed.status) throw new Error(parsed.description || parsed.message || 'Error adding to cart');

      updateCartSections(parsed);
      openCartDrawer(parsed);

      if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined'){
        publish(PUB_SUB_EVENTS.cartUpdate, { source: overlayId, productVariantId: variantId, cartData: parsed });
      }
    }

    // --- Original helper implementations (unchanged) -------------
    function getSectionsToRender(){
      return [
        { id:'main-cart-items',  section: document.getElementById('main-cart-items')?.dataset.id || 'main-cart-items', selector:'.js-contents' },
        { id:'cart-icon-bubble', section:'cart-icon-bubble', selector:'.shopify-section' },
        { id:'cart-live-region-text', section:'cart-live-region-text', selector:'.shopify-section' },
        { id:'main-cart-footer', section: document.getElementById('main-cart-footer')?.dataset.id || 'main-cart-footer', selector:'.js-contents' },
        { id:'cart-drawer', section:'cart-drawer', selector:'.shopify-section' }
      ];
    }

    function getSectionInnerHTML(html, selector){
      return new DOMParser().parseFromString(html,'text/html').querySelector(selector).innerHTML;
    }

    function updateCartSections(parsed){
      const cartItems = document.querySelector('cart-items');
      const cartDrawerWrapper = document.querySelector('cart-drawer');
      const cartFooter = document.getElementById('main-cart-footer');
      if (cartItems) cartItems.classList.toggle('is-empty', parsed.item_count === 0);
      if (cartFooter) cartFooter.classList.toggle('is-empty', parsed.item_count === 0);
      if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsed.item_count === 0);

      getSectionsToRender().forEach(section => {
        const elToReplace = document.getElementById(section.id)?.querySelector(section.selector) || document.getElementById(section.id);
        if (elToReplace && parsed.sections && parsed.sections[section.section]){
          elToReplace.innerHTML = getSectionInnerHTML(parsed.sections[section.section], section.selector);
        }
      });
      const cartStatus = document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
      if (cartStatus){
        cartStatus.setAttribute('aria-hidden', false);
        setTimeout(() => cartStatus.setAttribute('aria-hidden', true), 1000);
      }
    }

    function openCartDrawer(parsed){
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer){
        if (typeof cartDrawer.renderContents === 'function') cartDrawer.renderContents(parsed);
        else cartDrawer.open?.();
      }
    }

    /* ----------------------------------------------------------- */
    /*  PUBLIC API                                                  */
    /* ----------------------------------------------------------- */
    async function onAddClick(){
      const selected = Array.from(sizeInputs).find(i => i.checked);
      if (!selected) return alert('Bitte wählen Sie eine Größe.');
      const mapping = getMapping(selected.value);
      if (!mapping) return alert('Ungültige Größe ausgewählt.');

      // Check if variant is available for current quantity
      const requestedQty = currentQty || 1;
      const isAvailable = checkVariantAvailability(mapping.id, requestedQty);
      
      if (!isAvailable) {
        return alert('Diese Variante ist leider ausverkauft.');
      }

      try {
        setLoading(true);
        await addToCartAndRefresh(mapping.id, currentQty);
        overlay.classList.remove('silon-overlay--visible');
        document.body.classList.remove('overflow-hidden');
      } catch(err){
        console.error(err);
        alert('Fehler beim Hinzufügen zum Warenkorb. Bitte versuchen Sie es erneut.');
      } finally { setLoading(false); }
    }

    function show(){
      const args = Array.from(arguments);
      if (colorBased){
        [currentColor, currentQty, currentActualPrice, currentComparisonPrice] = args;
        if (!currentColor || !productSizes[currentColor]){
          console.error('[SilonSizeModal] Invalid color', currentColor);
          return;
        }
      } else {
        [currentQty, currentActualPrice, currentComparisonPrice] = args;
      }

      // Ensure variant data is loaded
      if (Object.keys(variantData).length === 0) {
        loadVariantData();
      }

      // Get variant availability map based on requested quantity
      const requestedQty = currentQty || 1;
      const availabilityMap = getVariantAvailabilityMap(requestedQty);
      
      // Mark unavailable variants in UI
      markUnavailableVariants(availabilityMap);
      
      // Select first available size (shifts default if needed)
      selectFirstAvailableSize(availabilityMap);

      // Update price display with selected size
      const selectedInput = overlay?.querySelector('input[name="size"]:checked');
      const selectedSize = selectedInput?.value || defaultSize;
      const mapping = getMapping(selectedSize);
      currentSurcharge = mapping?.surcharge || 0;
      updatePriceDisplay();

      overlay?.classList.add('silon-overlay--visible');
      document.body.classList.add('overflow-hidden');
    }

    return { show };
  }

  global.SilonSizeModalFactory = factory;
})(window);
